
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { toast } from '@/hooks/use-toast';
import { Json } from '@/integrations/supabase/types';

export type NotificationSetting = {
  notification_type: string;
  label: string;
  description: string;
  email_enabled: boolean;
  in_app_enabled: boolean;
  frequency: 'immediate' | 'daily' | 'never';
};

export function useUserNotificationSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<NotificationSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      
      // Direct query to the function without using execute_sql_query
      const { data, error } = await supabase
        .from('user_notification_settings')
        .select(`
          notification_type,
          email_enabled,
          in_app_enabled,
          frequency
        `)
        .eq('user_id', user.id);

      if (error) throw error;
      
      if (data && Array.isArray(data)) {
        // Transform the data to include labels and descriptions
        const transformedSettings = data.map(setting => {
          let label = '';
          let description = '';
          
          // Set label based on notification type
          switch (setting.notification_type) {
            case 'document_verified':
              label = 'Document Verification';
              description = 'Notifications when your identity documents are approved or rejected';
              break;
            case 'new_poll_published':
              label = 'New Poll Published';
              description = 'Notifications when new polls are published';
              break;
            case 'pollster_status_updated':
              label = 'Pollster Status Updates';
              description = 'Updates about your pollster application status';
              break;
            case 'poll_closed':
              label = 'Poll Closed';
              description = 'Notifications when polls you participated in are closed';
              break;
            case 'role_updated':
              label = 'Role Updates';
              description = 'Notifications when your account role changes';
              break;
            case 'account_suspended':
              label = 'Account Status';
              description = 'Notifications about account access limitations';
              break;
            case 'account_reactivated':
              label = 'Account Status';
              description = 'Notifications when your account is reactivated';
              break;
            default:
              label = setting.notification_type;
              description = 'System notification';
          }
          
          // Return properly typed notification setting
          return {
            notification_type: setting.notification_type,
            label,
            description,
            email_enabled: Boolean(setting.email_enabled),
            in_app_enabled: Boolean(setting.in_app_enabled),
            frequency: setting.frequency as 'immediate' | 'daily' | 'never'
          };
        });
        
        setSettings(transformedSettings);
      } else {
        setSettings([]);
      }
    } catch (error: any) {
      console.error('Error fetching notification settings:', error);
      toast({
        title: "Error loading settings",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSetting = (index: number, field: keyof Omit<NotificationSetting, 'notification_type' | 'label' | 'description'>, value: any) => {
    const newSettings = [...settings];
    newSettings[index] = {
      ...newSettings[index],
      [field]: value
    };
    setSettings(newSettings);
  };

  const saveSettings = async () => {
    if (!user?.id) return;
    
    try {
      setSaving(true);
      
      // Process each setting individually
      for (const setting of settings) {
        const { error } = await supabase
          .from('user_notification_settings')
          .update({
            email_enabled: setting.email_enabled,
            in_app_enabled: setting.in_app_enabled,
            frequency: setting.frequency
          })
          .eq('user_id', user.id)
          .eq('notification_type', setting.notification_type);

        if (error) throw error;
      }
      
      toast({
        title: "Settings saved",
        description: "Your notification preferences have been updated.",
      });
      
    } catch (error: any) {
      console.error('Error saving notification settings:', error);
      toast({
        title: "Error saving settings",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  return {
    settings,
    loading,
    saving,
    updateSetting,
    saveSettings,
    refreshSettings: fetchSettings
  };
}
